// === PushService (2.3.0) ==============================================
// 네이티브 푸시 — Capacitor + Firebase Cloud Messaging. drape 의 push-service.js
// 를 가져왔다. 서버 발송은 functions/notify.js (생성 완료, Gio 답장).
//
// 웹(PWA)은 등록하지 않는다 — 화면이 열려 있으면 Firestore 스냅샷이 바로 바꾼다.
//
//   users/{uid}/fcmTokens/{token} = { platform, createdAt }
// 기기마다 문서 하나 — 여러 기기가 따로 등록되고, 죽은 토큰은 서버가 지운다.
//
// 권한은 온보딩 투어가 끝난 직후에 묻는다 (Onboarding.jsx — 투어 전에 로그인을
// 안 했으면 로그인 직후 App.jsx). 2.3.0 첫 빌드는 생성을 맡기는 순간 물었는데,
// 그땐 사용자가 이미 앱을 내리는 중이라 창을 놓치거나 푸시가 올 때에야 허락을
// 누르게 됐다. iOS 는 한 번 거절하면 다시 못 묻는다.
//
// 앱 밖에서 해 둬야 실제로 날아간다:
//  - iOS: APNs 인증 키(.p8)를 Firebase 콘솔 → 프로젝트 설정 → Cloud Messaging 에 업로드,
//    Xcode 에서 Push Notifications capability (App.entitlements 의 aps-environment)
//  - Android: android/app/google-services.json (이미 있음)
import { Capacitor } from '@capacitor/core';
import { collection, doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase.js';

export const OPEN_ROUTE_EVENT = 'voda:open-route';

let listenersReady = false;
let tapHandlerReady = false;
let cachedToken = null;
// 알림을 눌러 앱이 켜졌는데 라우터·로그인이 아직이면 여기 둔다.
let pendingNav = null;

function routeForNotification(data = {}) {
  if (data.type === 'design' && data.designId) return `/designs/${data.designId}`;
  if (data.type === 'gio') return '/gio';
  if (data.type === 'create') return '/create';
  return null;
}

// 플러그인은 Capacitor proxy 라 async 함수의 반환값으로 내보내면 런타임이 `.then`
// 을 프로브하고 iOS 브리지가 "FirebaseMessaging.then() is not implemented" 로
// 거부한다 (firebase.js 의 nativeAnalytics 와 같은 함정 — 2.3.0 첫 빌드에서 권한
// 창이 한 번도 안 뜬 원인). 평범한 { M } 객체로 감싸 돌려준다.
async function plugin() {
  const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
  return { M: FirebaseMessaging };
}

async function persistToken(uid, token) {
  if (!uid || !token) return;
  cachedToken = token;
  await setDoc(
    doc(collection(db, 'users', uid, 'fcmTokens'), token),
    { platform: Capacitor.getPlatform(), createdAt: serverTimestamp() },
    { merge: true },
  );
}

// 권한이 있는 상태에서 토큰을 받아 저장하고, 토큰 갱신을 듣는다.
async function register(FirebaseMessaging, uid) {
  if (!listenersReady) {
    listenersReady = true;
    FirebaseMessaging.addListener('tokenReceived', async (event) => {
      const u = auth.currentUser;
      if (!u || u.isAnonymous || !event?.token) return;
      await persistToken(u.uid, event.token).catch(() => {});
    });
  }
  // @capacitor/push-notifications 가 아니라 이 플러그인을 쓰는 이유: iOS 에서
  // 그쪽은 APNs 토큰을 주는데 서버(admin.messaging)는 FCM 토큰이 필요하다.
  const { token } = await FirebaseMessaging.getToken();
  if (token) await persistToken(uid, token);
}

export const PushService = {
  // 앱 시작 직후 (로그인 전) 에 건다 — 콜드 스타트의 알림 탭은 부팅 직후 한 번만 온다.
  async initTapHandler() {
    if (!Capacitor.isNativePlatform() || tapHandlerReady) return;
    tapHandlerReady = true;
    try {
      const { M: FirebaseMessaging } = await plugin();
      FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        const route = routeForNotification(event?.notification?.data || {});
        if (!route) return;
        // window.location 으로 가면 스플래시·인증 부팅을 다시 돌아 화면이 멈춘다.
        // App.jsx 가 이 이벤트(웜)와 consumePendingNav(콜드)로 라우터에 넘긴다.
        pendingNav = route;
        try { window.dispatchEvent(new CustomEvent(OPEN_ROUTE_EVENT, { detail: route })); } catch { /* ignore */ }
      });
    } catch (err) {
      tapHandlerReady = false;
      console.warn('push initTapHandler failed:', err.message);
    }
  },

  consumePendingNav() {
    const r = pendingNav;
    pendingNav = null;
    return r;
  },

  // 로그인 상태가 바뀔 때마다 — 이미 허락된 경우에만 조용히 등록한다. 묻지 않는다.
  async ensureRegistered() {
    if (!Capacitor.isNativePlatform()) return;
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return;
    try {
      const { M: FirebaseMessaging } = await plugin();
      const perm = await FirebaseMessaging.checkPermissions();
      if (perm.receive !== 'granted') return;
      await register(FirebaseMessaging, user.uid);
    } catch (err) {
      console.warn('push ensureRegistered failed:', err.message);
    }
  },

  // 알림이 의미 있는 순간에 부른다. 이미 정해졌으면(허락/거절) 다시 묻지 않는다.
  async askPermission() {
    if (!Capacitor.isNativePlatform()) return false;
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return false;
    try {
      const { M: FirebaseMessaging } = await plugin();
      let perm = await FirebaseMessaging.checkPermissions();
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await FirebaseMessaging.requestPermissions();
      }
      if (perm.receive !== 'granted') return false;
      await register(FirebaseMessaging, user.uid);
      return true;
    } catch (err) {
      console.warn('push askPermission failed:', err.message);
      return false;
    }
  },

  // 로그아웃 — 이전 사용자의 알림이 이 기기로 계속 오지 않게.
  async unregister(uid) {
    if (uid && cachedToken) {
      try { await deleteDoc(doc(db, 'users', uid, 'fcmTokens', cachedToken)); }
      catch (err) { console.warn('push unregister failed:', err.message); }
    }
    cachedToken = null;
  },
};

export default PushService;
